/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */
import { merge } from '@adobe-commerce/elsie/lib';
import { config } from '@/pdp/api';
import { ProductModel, Option } from '@/pdp/data/models';
import { ProductType, ProductViewType } from '../models/acdl-models';

export function transformProductData(
  data: any,
  options?: {
    preselectFirstOption?: boolean;
  }
): ProductModel | null {
  const isBundle =
    data?.options?.[0]?.values?.[0]?.__typename ===
    'ProductViewOptionValueProduct';
  const optionUIDs = getOptionUIDs(
    data,
    isBundle,
    options?.preselectFirstOption
  );
  const productType =
    data?.__typename === ProductViewType.SimpleProductView
      ? ProductType.SimpleProduct
      : ProductType.ComplexProduct;

  const productData = data
    ? ({
        name: data.name,
        sku: data.sku,
        isBundle,
        addToCartAllowed: data.addToCartAllowed,
        inStock: data.inStock,
        shortDescription: data.shortDescription,
        metaDescription: data.metaDescription,
        metaKeyword: data.metaKeyword,
        metaTitle: data.metaTitle,
        description: data.description,
        images: transformImages(data),
        videos: transformVideos(data),
        prices: transformPrices(data, isBundle, optionUIDs),
        attributes: transformAttributes(data),
        options: applyOptionsTransformer(transformOptions(data, isBundle)),
        optionUIDs,
        url: data.url,
        urlKey: data.urlKey,
        externalId: data.externalId,
        externalParentId: data.externalParentId,
        variantSku: data.variantSku,
        productType,
      } as ProductModel)
    : null;

  // extend the model with custom transformer
  return merge(
    productData,
    config.getConfig()?.models?.ProductDetails?.transformer?.(data) ??
      config.getConfig()?.models?.ProductDetails?.transform?.(data)
  );
}

function applyOptionsTransformer(
  options: Option[] | undefined
): Option[] | undefined {
  if (!options) return undefined;

  const optionsTransformer =
    config.getConfig()?.models?.ProductOptions?.optionsTransformer;

  if (!optionsTransformer) {
    return options;
  }

  try {
    const transformed = optionsTransformer(options);
    return transformed ?? options;
  } catch (error) {
    console.error(
      'product-transform: optionsTransformer threw an error, using original options',
      error
    );
    return options;
  }
}

function transformImages(data: any) {
  return data.images
    ?.filter((image: any) => !image.roles?.includes('hide_from_pdp'))
    ?.map((image: any) => {
      // Remove protocol from image URL
      image.url = image.url.replace(/^https?:/, '');

      return image;
    });
}

function transformVideos(data: any) {
  return data.videos?.map((video: any) => ({
    url: video.url?.replace(/^https?:/, ''),
    title: video.title,
    description: video.description,
    preview: video.preview
      ? {
          url: video.preview.url?.replace(/^https?:/, ''),
          label: video.preview.label,
          roles: video.preview.roles,
        }
      : undefined,
  }));
}

function transformAttributes(data: any) {
  return (
    data.attributes
      // only show attributes that are visible in PDP
      ?.filter(({ roles }: any) => roles?.indexOf('visible_in_pdp') !== -1)
      // transform attribute values
      ?.map(({ label, value, name }: any) => {
        // Special handling for ac_giftcard attribute
        if (name?.startsWith('ac_')) {
          try {
            let parsedValue;

            if (typeof value === 'string') {
              parsedValue = JSON.parse(value);
            } else if (typeof value === 'object') {
              parsedValue = value;
            }
            // Transform specific snake_case fields to camelCase
            const transformedValue = transformSnakeToCamel(parsedValue);

            return {
              id: name,
              label,
              value: JSON.stringify(transformedValue),
            };
          } catch {
            // If parsing fails, return original value
            return {
              id: name,
              label,
              value: value.toString().split(',').join(', '),
            };
          }
        }

        return {
          id: name,
          label,
          value: value.toString().split(',').join(', '),
        };
      })
  );
}

function transformSnakeToCamel(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(transformSnakeToCamel);
  }

  if (obj !== null && typeof obj === 'object') {
    const transformed: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Only transform specific snake_case fields to camelCase
      let camelKey = key;
      if (key === 'validation_rule') {
        camelKey = 'validationRule';
      } else if (key === 'min_length') {
        camelKey = 'minLength';
      } else if (key === 'max_length') {
        camelKey = 'maxLength';
      } else if (key === 'purchase_separately') {
        camelKey = 'purchaseSeparately';
      }

      transformed[camelKey] = transformSnakeToCamel(value);
    }
    return transformed;
  }

  return obj;
}

function transformOptions(data: any, isBundle?: boolean) {
  const { options, optionUIDs } = data;

  return options?.map(({ id, title, required, multi, values }: any) => {
    const typename = values?.[0]?.__typename;
    let type: string = values?.[0]?.type;

    if (type) {
      type = type
        .replace('COLOR_HEX', 'color')
        .replace('TEXT', 'text')
        .replace('IMAGE', 'image');
    } else {
      type = 'dropdown';
    }

    return {
      id,
      type,
      typename,
      label: title,
      required,
      multiple: multi,
      items: isBundle
        ? mapBundleOptionValues(values, optionUIDs)
        : mapOptionValues(values, optionUIDs, type),
    };
  });
}

function mapBundleOptionValues(values: any, optionUIDs: string[]) {
  return values
    ?.filter(({ enabled }: any) => typeof enabled === 'undefined' || enabled)
    .map(({ id, title, inStock, isDefault, product }: any) => {
      return {
        id,
        inStock,
        label: title,
        selected: optionUIDs?.indexOf(id) > -1 || isDefault,
        value: id,
        product,
      };
    });
}

function mapOptionValues(values: any, optionUIDs: string[], type: string) {
  return values?.map(({ id, title, inStock, value }: any) => {
    return {
      id,
      inStock,
      label: title,
      selected: optionUIDs?.indexOf(id) > -1,
      value:
        type?.toLowerCase() === 'dropdown'
          ? id
          : value?.replace(/^https?:/, ''),
    };
  });
}

function transformPrices(
  data: any,
  isBundle: boolean,
  defaultOptionUIDs?: string[]
) {
  const { price, priceRange, options, optionUIDs = defaultOptionUIDs } = data;

  if (!price && !priceRange) {
    return null;
  }

  let { __typename: type } = data;

  function getSimpleProductPrices() {
    const regularPrice = price?.regular?.amount?.value;
    const finalPrice =
      price?.final?.amount?.value ?? price?.regular?.amount?.value;

    const currency =
      price?.regular?.amount?.currency === 'NONE' //temporary fix if there is no currency
        ? 'USD'
        : price?.regular?.amount?.currency;

    return [regularPrice, finalPrice, finalPrice, currency];
  }

  function getComplexProductPrices() {
    const finalMinimumPrice = priceRange?.minimum?.final?.amount?.value;
    const finalMaximumPrice = priceRange?.maximum?.final?.amount?.value;
    let regularPrice;

    if (
      priceRange?.minimum?.regular?.amount?.value ===
      priceRange?.maximum?.regular?.amount?.value
    ) {
      regularPrice = priceRange?.minimum?.regular?.amount?.value;
    }

    const currency =
      priceRange?.minimum?.final?.amount?.currency === 'NONE'
        ? 'USD'
        : priceRange?.minimum?.final?.amount?.currency;

    return [regularPrice, finalMinimumPrice, finalMaximumPrice, currency];
  }

  function getBundleProductPrice() {
    // Currently the Catalog Service does not have support for the individual prices of the options of a fixed bundle,
    // so the price used in UI is taken from the priceRange object.

    // The dynamic bundle has full support in Catalog Service.
    // If there are pre-selected values of the option, then the starting price is the sum of those and
    // after each selection, the price is updated with the sum of prices for the selected options.
    // This is not supported by default and needs to be updated in the drop-in upon each selection.

    let maxSumRegular = 0;
    let bundlePrice = 0;
    options?.forEach((option: any) => {
      const values = option?.values;
      if (values && Array.isArray(values)) {
        const regularAmounts = values
          .map((value) => value?.product?.price?.regular?.amount?.value)
          .filter((amount) => amount !== undefined);

        const maxRegularAmount =
          regularAmounts.length > 0 ? Math.max(...regularAmounts) : 0;
        maxSumRegular += maxRegularAmount;
      }
      option?.values?.forEach((value: any) => {
        if (optionUIDs?.includes(value.id)) {
          bundlePrice += value?.product?.price?.final?.amount?.value;
        }
      });
    });

    const finalMinimumPrice = priceRange?.minimum?.final?.amount?.value;
    const finalMaximumPrice = priceRange?.maximum?.final?.amount?.value;
    let regularPrice;

    if (
      priceRange?.minimum?.regular?.amount?.value ===
      priceRange?.maximum?.regular?.amount?.value
    ) {
      regularPrice = priceRange?.minimum?.regular?.amount?.value;
    }

    const currency =
      priceRange?.minimum?.final?.amount?.currency === 'NONE'
        ? 'USD'
        : priceRange?.minimum?.final?.amount?.currency;

    if (isBundle && optionUIDs?.length < 1) {
      return [regularPrice, finalMinimumPrice, finalMaximumPrice, currency];
    }

    return maxSumRegular === priceRange?.maximum?.regular?.amount?.value
      ? [bundlePrice, bundlePrice, bundlePrice, currency]
      : [regularPrice, finalMinimumPrice, finalMaximumPrice, currency];
  }

  const [regularPrice, finalMinimumPrice, finalMaximumPrice, currency] =
    type === 'SimpleProductView'
      ? getSimpleProductPrices()
      : isBundle
      ? getBundleProductPrice()
      : getComplexProductPrices();

  const visible =
    type === 'SimpleProductView'
      ? price?.roles?.includes('visible')
      : priceRange?.maximum?.roles?.includes('visible') &&
        priceRange?.minimum?.roles?.includes('visible');

  function transformTiers(tiers: any[]): any[] {
    if (!tiers || !Array.isArray(tiers)) {
      return [];
    }

    return tiers
      .map((tier) => ({
        tier: {
          amount: tier.tier?.amount?.value,
          currency: tier.tier?.amount?.currency || currency,
        },
        quantity: tier.quantity[0]?.gte || tier.quantity[0]?.lt || 0,
      }))
      .sort((a, b) => a.quantity - b.quantity);
  }

  if (finalMaximumPrice && finalMinimumPrice === finalMaximumPrice) {
    const hasDiscount = regularPrice && finalMinimumPrice !== regularPrice;

    return {
      regular: {
        amount: regularPrice,
        currency,
        variant: hasDiscount ? 'strikethrough' : 'default',
      },
      final: {
        amount: finalMaximumPrice,
        currency,
        variant: 'default',
      },
      tiers: transformTiers(price?.tiers),
      visible,
    };
  }

  return {
    final: {
      minimumAmount: finalMinimumPrice,
      maximumAmount: finalMaximumPrice,
      currency,
    },
    tiers: transformTiers(price?.tiers),
    visible,
  };
}

function getOptionUIDs(
  data: any,
  isBundle?: boolean,
  preselectFirstOption?: boolean
): string[] {
  if (isBundle) {
    const selections: Array<string | null> = data?.options?.map(
      ({ values }: any) => {
        const defaultSelection =
          values?.find(({ isDefault }: any) => isDefault)?.id ?? null;
        const preselectedFirstOption = preselectFirstOption
          ? values[0]?.id
          : null;
        return defaultSelection || preselectedFirstOption;
      }
    );

    return selections.filter((selection) => selection !== null) as string[];
  }

  return data?.optionUIDs;
}
